/**
 * Storage layer for search history using localStorage.
 * Handles serialization/deserialization and error handling.
 */

import { SearchHistoryItem } from "./types";

const DEFAULT_STORAGE_KEY = "search.history";

/**
 * Get search history from localStorage.
 * Returns empty array if no history exists or on error.
 */
export function getHistory(storageKey: string = DEFAULT_STORAGE_KEY): SearchHistoryItem[] {
  try {
    const data = localStorage.getItem(storageKey);
    if (!data) {
      return [];
    }

    const parsed = JSON.parse(data);

    // Validate that parsed data is an array
    if (!Array.isArray(parsed)) {
      console.warn(`Search history data in ${storageKey} is not an array, returning empty array`);
      return [];
    }

    // Validate each item has required fields
    const validItems = parsed.filter(
      (item): item is SearchHistoryItem =>
        item &&
        typeof item === "object" &&
        typeof item.query === "string" &&
        typeof item.timestamp === "number" &&
        !isNaN(item.timestamp)
    );

    if (validItems.length !== parsed.length) {
      console.warn(`Some search history items in ${storageKey} are invalid, filtering them out`);
    }

    return validItems;
  } catch (error) {
    // localStorage might be unavailable (private mode) or corrupted
    console.warn(`Failed to read search history from ${storageKey}:`, error);
    return [];
  }
}

/**
 * Save search history to localStorage.
 * Returns true on success, false on error.
 */
export function saveHistory(
  history: SearchHistoryItem[],
  storageKey: string = DEFAULT_STORAGE_KEY
): boolean {
  try {
    const data = JSON.stringify(history);
    localStorage.setItem(storageKey, data);
    return true;
  } catch (error) {
    // localStorage might be full or unavailable
    console.warn(`Failed to save search history to ${storageKey}:`, error);
    return false;
  }
}

/**
 * Clear all search history from localStorage.
 * Returns true on success, false on error.
 */
export function clearHistory(storageKey: string = DEFAULT_STORAGE_KEY): boolean {
  try {
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.warn(`Failed to clear search history from ${storageKey}:`, error);
    return false;
  }
}

/**
 * Check if localStorage is available and writable.
 * Useful for feature detection.
 */
export function isStorageAvailable(): boolean {
  try {
    const testKey = `__test_${Date.now()}`;
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}
